import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { BuildingIcon } from "@lucide/icons";

import { Button } from "simplydo/react/ui/button";
import { Card, CardContent, CardDescription } from "simplydo/react/ui/card";
import { H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import IdeaBusinessProfileModal from "components/lib/IdeaBusinessProfileModal/IdeaBusinessProfileModal";

import InviteeChip from "./InviteeChip";
import type { HomeInvitation, InviteCardProps } from "./types";

const IdeaBusinessProfileInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, ideaBusinessProfile, createdByCurrentUser } = invitation;
  const [viewingProfile, setViewingProfile] = useState<
    HomeInvitation["ideaBusinessProfile"] | null
  >(null);
  const user = useAppSelector((state) => state.user);
  const accept = useCallback(() => {
    util
      .confirm(
        "Join business profile",
        `Are you sure you want to join "${ideaBusinessProfile?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success("Joined business profile");
          },
          () => toast.error("Error joining business profile"),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, ideaBusinessProfile?.name]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject business profile invitation",
        `Are you sure you want to reject the invitation to join "${ideaBusinessProfile?.name || "Deleted profile"}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, ideaBusinessProfile?.name]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove business profile invitation",
        `Are you sure you want to remove this invitation to join "${ideaBusinessProfile?.name || "Deleted business profile"}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [removeInvite, invitation, ideaBusinessProfile?.name]);

  const canAccept = invitation.canAccept && !!ideaBusinessProfile;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      {viewingProfile ? (
        <IdeaBusinessProfileModal
          businessProfile={viewingProfile}
          onClose={() => setViewingProfile(null)}
        />
      ) : null}
      <div>
        {cardView ? (
          <div
            onClick={() =>
              ideaBusinessProfile && setViewingProfile(ideaBusinessProfile)
            }
          >
            <H3 style={{ marginBottom: "0.3em" }}>
              {ideaBusinessProfile?.name || "Deleted profile"}
            </H3>
          </div>
        ) : (
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={BuildingIcon} />
            Join {ideaBusinessProfile?.name || `Deleted profile`}
          </H2>
        )}
        <CardDescription>
          {createdByCurrentUser ? (
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText="to join this business profile"
            />
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText="has invited you to join this business profile"
            />
          )}
        </CardDescription>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? { display: "flex", justifyContent: "flex-end" }
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && ideaBusinessProfile && (
            <Button
              color="green"
              disabled={!ideaBusinessProfile}
              onClick={() =>
                ideaBusinessProfile && setViewingProfile(ideaBusinessProfile)
              }
              variant="outline"
            >
              View business profile
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!ideaBusinessProfile && (
                <Message color="red" style={{ width: "100%" }}>
                  This invitation to join is no longer valid because the
                  business profile has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                  variant="outline"
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && canAccept ? (
                <Button color="green" onClick={accept} variant="outline">
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default IdeaBusinessProfileInviteCard;
